import {Asset} from '@hconnect/common/types'
import {dataTestId} from '@hconnect/uikit'
import {DeleteOutline} from '@mui/icons-material'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography
} from '@mui/material'
import {FC, MouseEvent, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {useDeleteGrindingCircuit} from '../../hooks/grindingCircuits'
import {GrindingCircuit} from '../../types'

import {GrindingCircuitDeleteMessage} from './GrindingCircuitDeleteMessage'
import {GrindingCircuitsEdit} from './GrindingCircuitsEdit'

import {ListData, StateList} from '@settings/modules/common/components'
import {useConfirmDialog} from '@settings/modules/common/providers'
import {useUrlParam} from '@settings/modules/routing'

type GrindingCircuitsListProps = {
  grindingCircuits?: GrindingCircuit[]
  assets?: Asset[]
  isLoading: boolean
  isFetching: boolean
}

const MIN_MENU_BUTTON_WIDTH_IN_PX = 200

export const mapData = (
  data?: GrindingCircuit[],
  assets?: Asset[]
): ListData<number>[] | undefined => {
  if (!data) return undefined

  const assetMap = new Map(assets?.map(({id, name}) => [id, name]))

  return data.map((item) => {
    const assetNames = item.assetIds.map((id) => assetMap.get(id) || String(id))

    return {
      id: item.id,
      title: item.name,
      label: !assetNames.length ? item.assetIds.join(', ') : assetNames.join(', ')
    }
  })
}

export const GrindingCircuitsList: FC<GrindingCircuitsListProps> = ({
  grindingCircuits,
  assets,
  isLoading,
  isFetching
}) => {
  const {t} = useTranslation()
  const plantCode = useUrlParam('plantCode')
  const {openDialog, closeDialog} = useConfirmDialog()
  const {mutate: deleteGrindingUnit, isPending: isDeleteLoading} =
    useDeleteGrindingCircuit(closeDialog)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedItem, setSelectedItem] = useState<ListData<number> | null>(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false)

  const mappedData = useMemo(() => mapData(grindingCircuits, assets), [assets, grindingCircuits])
  const selectedGrindingCircuit = grindingCircuits?.find(({id}) => id === selectedItem?.id)

  const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>, item: ListData<number>) => {
    setAnchorEl(event.currentTarget)
    setSelectedItem(item)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setSelectedItem(null)
  }

  const openEditDialog = () => {
    setAnchorEl(null)
    setIsEditDialogOpen(true)
  }

  const openRemoveDialog = () => {
    setAnchorEl(null)
    if (!selectedItem) return
    openDialog({
      title: t('plantSetup.grindingCircuit.label.shouldDelete'),
      mainAction: {
        color: 'error',
        text: t('common.delete'),
        icon: <DeleteOutline />,
        onAction: () => deleteGrindingUnit({plantCode, grindingCircuitId: selectedItem?.id})
      },
      additionalContent: (
        <GrindingCircuitDeleteMessage title={selectedItem.title} label={selectedItem.label} />
      )
    })
  }

  return !isLoading && !isFetching && !mappedData?.length ? (
    <Typography px={2} mb={2} color="grey.700">
      {t('plantSetup.grindingCircuit.label.noGrindingCircuit')}
    </Typography>
  ) : (
    <>
      <StateList
        data={mappedData}
        isLoading={isLoading}
        isFetching={isFetching || isDeleteLoading}
        secondaryAction={(grindingCircuit) => (
          <IconButton
            edge="end"
            color="primary"
            aria-label="more"
            onClick={(e) => handleOpenMenu(e, grindingCircuit)}
            {...dataTestId(`grinding_circuit_${grindingCircuit.id}_menu_button`)}
          >
            <MoreVertIcon />
          </IconButton>
        )}
        data-test-id="grinding-circuits-list"
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
      >
        <MenuItem
          sx={{minWidth: MIN_MENU_BUTTON_WIDTH_IN_PX}}
          onClick={openEditDialog}
          {...dataTestId('grinding_circuit_edit_button')}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('plantSetup.grindingCircuit.action.edit')}</ListItemText>
        </MenuItem>
        <Divider sx={{mx: 2}} />
        <MenuItem
          sx={{minWidth: MIN_MENU_BUTTON_WIDTH_IN_PX}}
          onClick={openRemoveDialog}
          {...dataTestId('grinding_circuit_delete_button')}
        >
          <ListItemIcon>
            <DeleteOutlineOutlinedIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{color: 'error.main'}}>{t('common.delete')}</ListItemText>
        </MenuItem>
      </Menu>
      <GrindingCircuitsEdit
        assets={assets}
        setIsDialogOpen={setIsEditDialogOpen}
        isDialogOpen={isEditDialogOpen}
        defaultValues={selectedGrindingCircuit}
      />
    </>
  )
}
